import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  GraduationCap,
  Users,
  Trophy,
  BookOpen,
  Star,
  ArrowRight,
  Sparkles,
  Phone,
  Mail,
  MapPin,
  Play,
  ChevronLeft,
  ChevronRight,
  HandCoins,
  Building2,
  Brain,
  Handshake,
  ChartColumnIncreasing,
  Banknote
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

import heroImage from '../assets/male-student-smiling-blue-wall.jpg';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

const stats = [
  { icon: Users, value: '5000+', label: 'Students Mentored' },
  { icon: Trophy, value: '90%+', label: 'Academic Excellence' },
  { icon: BookOpen, value: '20+', label: 'Programs & Courses' },
  { icon: Star, value: '10+', label: 'Years of Educational Commitment' },
];

const features = [
  { icon: GraduationCap, title: 'Strong Foundation for Future Success', description: 'Our Foundation Program for Classes 7th, 8th, 9th, and 10th focuses on building core concepts, analytical thinking, and academic discipline that prepare students for higher education and competitive exams.' },
  { icon: Users, title: 'Experienced Faculty', description: 'Learn from dedicated educators committed to helping students understand concepts thoroughly and perform confidently in examinations.' },
  { icon: BookOpen, title: 'Comprehensive Learning Approach', description: 'We combine classroom teaching, regular assessments, doubt-solving sessions, and performance tracking to ensure continuous improvement.' },
  { icon: Brain, title: 'Focus on Individual Growth', description: 'Every student learns differently. Our personalized attention helps students identify strengths, overcome challenges, and achieve academic goals.' },
  { icon: Trophy, title: 'Exam-Oriented Preparation', description: 'From school examinations to competitive entrance tests, we equip students with the skills and strategies required to succeed.' },
];

const HomePage = () => {
  const [courses, setCourses] = useState([]);
  const [toppers, setToppers] = useState([]);
  const [showAllToppers, setShowAllToppers] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [notices, setNotices] = useState([]);
  const [inquiryForm, setInquiryForm] = useState({
    name: '',
    email: '',
    phone: '',
    course_interest: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [coursesRes, toppersRes, testimonialsRes, noticesRes] = await Promise.all([
          axios.get(`${API_URL}/api/courses?featured=true`),
          axios.get(`${API_URL}/api/toppers`),
          axios.get(`${API_URL}/api/testimonials`),
          axios.get(`${API_URL}/api/notices`)
        ]);
        setCourses(coursesRes.data.slice(0, 4));
        const sorted = [...toppersRes.data].sort((a, b) => {
          if (a.rank != null && b.rank != null) return a.rank - b.rank;
          if (a.rank != null) return -1;
          if (b.rank != null) return 1;
          return 0;
        });
        setToppers(sorted);
        setTestimonials(testimonialsRes.data.slice(0, 3));
        setNotices(noticesRes.data.slice(0, 5));
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/inquiries`, inquiryForm);
      toast.success('Inquiry submitted successfully! We will contact you soon.');
      setInquiryForm({ name: '', email: '', phone: '', course_interest: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-rose-50 to-slate-50 py-20 lg:py-32">
        <div className="hero-pattern absolute inset-0"></div>
        <div className="max-w-7xl mx-auto px-4 md:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeUp} className="text-center lg:text-left">
              <Badge className="mb-4 bg-secondary text-secondary-foreground" data-testid="hero-badge">
                <Sparkles className="w-3 h-3 mr-1" /> Admissions Open {new Date().getFullYear()}
              </Badge>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-5xl font-bold text-foreground leading-tight mb-6">
                Shape Your Future with the<br />
                <span className="text-primary">Right Foundation</span>
              </h1>
              <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto lg:mx-0">
                Empowering students from Classes 7th to 12th with expert guidance, conceptual learning, and result-oriented preparation for Boards, JEE, NEET, MHT-CET, and beyond.
              </p>
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                <Button size="lg" asChild className="bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20" data-testid="hero-explore-btn">
                  <Link to="/contact">
                    Book a Free Counseling Session
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild data-testid="hero-demo-btn">
                  <Link to="/courses">
                    <BookOpen className="w-4 h-4 mr-2" />
                    Explore Courses
                  </Link>
                </Button>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={heroImage}
                  alt="Students studying"
                  className="w-full h-[400px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent"></div>
              </div>
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 hidden md:block">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <Trophy className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-xl">5000+</p>
                    <p className="text-sm text-muted-foreground">Students Mentored</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-primary">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <stat.icon className="w-8 h-8 mx-auto mb-3 text-secondary" />
                <p className="font-heading text-3xl md:text-4xl font-bold text-white">{stat.value}</p>
                <p className="text-sm text-primary-foreground/80">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <motion.div {...fadeUp}>
            <Badge className="mb-4">Our Philosophy</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Build Strong Foundations. <span className="text-primary">Achieve Bigger Goals.</span>
            </h2>
            <div className="space-y-6 text-lg text-muted-foreground leading-relaxed text-left md:text-center">
              <p>
                At Caliber Nova, we believe that academic success begins with a strong foundation.
                We provide expert coaching for students from 7th to 12th, helping them develop conceptual clarity, confidence, and problem-solving skills that lead to long-term success.
              </p>
              <p>
                Whether your goal is to excel in school exams, prepare for JEE, NEET, MHT-CET, or pursue professional courses, our structured learning approach ensures every student receives the guidance they need to achieve their potential.
              </p>
              <p className="font-semibold text-primary pt-2">
                Enroll Today & Take the First Step Towards Academic Excellence.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <Badge className="mb-4">Why Choose Us</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              The Caliber Nova <span className="text-primary">Advantage</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Every coaching institute makes promises. We make a guarantee. Here’s what makes us the most trusted name in Nagpur for JEE/NEET preparation.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full card-hover border-0 shadow-sm bg-slate-50 hover:bg-white" data-testid={`feature-${index}`}>
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                      <feature.icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-heading font-semibold text-lg mb-2">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Courses */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div {...fadeUp} className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-4">
            <div>
              <Badge className="mb-4">Our Programs</Badge>
              <h2 className="font-heading text-3xl md:text-4xl font-bold">
                Featured <span className="text-primary">Courses</span>
              </h2>
            </div>
            <Button variant="outline" asChild data-testid="view-all-courses">
              <Link to="/courses">
                View All Courses
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {courses.length > 0 ? courses.map((course, index) => (
              <motion.div
                key={course.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full card-hover overflow-hidden" data-testid={`course-card-${index}`}>
                  <div className="h-40 bg-gradient-to-br from-primary to-accent relative overflow-hidden">
                    {course.image_url ? (
                      <img src={course.image_url.startsWith('http') ? course.image_url : `${API_URL}${course.image_url.startsWith('/') ? '' : '/'}${course.image_url}`} alt={course.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <BookOpen className="w-12 h-12 text-white/50" />
                      </div>
                    )}
                  </div>
                  <CardContent className="p-5">
                    <Badge variant="secondary" className="mb-2">{course.category}</Badge>
                    <h3 className="font-heading font-semibold text-lg mb-2 line-clamp-2">{course.name}</h3>
                    <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
                      <span>{course.duration}</span>
                      <span>{course.course_type}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-heading font-bold text-lg text-primary">₹{course.fee?.toLocaleString()}</span>
                      </div>
                      <Button size="sm" asChild>
                        <Link to={`/courses/${course.id}`}>View Details</Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )) : (
              // Placeholder cards
              [1, 2, 3, 4].map((i) => (
                <Card key={i} className="h-full overflow-hidden" data-testid={`course-placeholder-${i}`}>
                  <div className="h-40 bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                    <BookOpen className="w-12 h-12 text-white/50" />
                  </div>
                  <CardContent className="p-5">
                    <Badge variant="secondary" className="mb-2">JEE/NEET</Badge>
                    <h3 className="font-heading font-semibold text-lg mb-2">Premium Course Package</h3>
                    <p className="text-sm text-muted-foreground mb-4">Comprehensive preparation with expert guidance</p>
                    <Button size="sm" asChild className="w-full">
                      <Link to="/courses">Explore</Link>
                    </Button>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Results & Toppers */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <Badge className="mb-4">Our Results</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Our <span className="text-primary">Top Rankers</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Our students have consistently secured top ranks in JEE and NEET examinations.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {toppers.length > 0 ? toppers.slice(0, 6).map((topper, index) => (
              <motion.div
                key={topper.id}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="text-center"
              >
                <div className="relative mb-3">
                  <div className="w-20 h-20 mx-auto rounded-full bg-slate-200 overflow-hidden">
                    {topper.photo_url ? (
                      <img src={topper.photo_url.startsWith('http') ? topper.photo_url : `${API_URL}${topper.photo_url}`} alt={topper.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-primary/10">
                        <Users className="w-8 h-8 text-primary/50" />
                      </div>
                    )}
                  </div>
                  {topper.rank && (
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-secondary text-secondary-foreground text-xs font-bold px-2 py-1 rounded-full">
                      AIR {topper.rank}
                    </div>
                  )}
                </div>
                <h4 className="font-medium text-sm mt-1">{topper.name}</h4>
                <p className="text-xs text-muted-foreground">{topper.exam}{topper.year ? ` ${topper.year}` : ''}</p>
              </motion.div>
            )) : (
              [1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="text-center">
                  <div className="relative mb-3">
                    <div className="w-20 h-20 mx-auto rounded-full bg-slate-100 flex items-center justify-center">
                      <Users className="w-8 h-8 text-slate-300" />
                    </div>
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-secondary text-secondary-foreground text-xs font-bold px-2 py-1 rounded-full">
                      AIR {i}
                    </div>
                  </div>
                  <h4 className="font-medium text-sm">Top Ranker</h4>
                  <p className="text-xs text-muted-foreground">JEE Advanced 2024</p>
                </div>
              ))
            )}
          </div>

          {toppers.length > 0 && (
            <div className="text-center mt-12">
              <Button variant="outline" onClick={() => setShowAllToppers(true)} data-testid="view-all-results">
                View All Results
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* All Toppers Dialog */}
          <Dialog open={showAllToppers} onOpenChange={setShowAllToppers}>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Our Top Rankers</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6 pt-2">
                {toppers.map((topper, index) => (
                  <motion.div
                    key={topper.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.2, delay: index * 0.03 }}
                    className="text-center"
                  >
                    <div className="relative mb-3">
                      <div className="w-20 h-20 mx-auto rounded-full bg-slate-200 overflow-hidden">
                        {topper.photo_url ? (
                          <img src={topper.photo_url.startsWith('http') ? topper.photo_url : `${API_URL}${topper.photo_url}`} alt={topper.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-primary/10">
                            <Users className="w-8 h-8 text-primary/50" />
                          </div>
                        )}
                      </div>
                      {topper.rank && (
                        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-secondary text-secondary-foreground text-xs font-bold px-2 py-1 rounded-full whitespace-nowrap">
                          AIR {topper.rank}
                        </div>
                      )}
                    </div>
                    <h4 className="font-medium text-sm mt-1">{topper.name}</h4>
                    <p className="text-xs text-muted-foreground">{topper.exam}{topper.year ? ` ${topper.year}` : ''}</p>
                  </motion.div>
                ))}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && <section className="py-20 bg-primary">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <Badge className="mb-4 bg-secondary text-secondary-foreground">Reviews</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              What Our Students & Parents Say
            </h2>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              Real experiences from students and parents who have chosen Caliber Nova for academic excellence and future success.
            </p>
          </motion.div>

          {(() => {
            const items = testimonials;
            const prev = () => setTestimonialIndex((i) => (i - 1 + items.length) % items.length);
            const next = () => setTestimonialIndex((i) => (i + 1) % items.length);
            return (
              <div className="relative max-w-3xl mx-auto">
                {/* Carousel track */}
                <div className="overflow-hidden rounded-2xl">
                  <div
                    className="flex transition-transform duration-400 ease-in-out"
                    style={{ transform: `translateX(-${testimonialIndex * 100}%)` }}
                  >
                    {items.map((testimonial, index) => (
                      <div key={testimonial.id} className="w-full shrink-0" data-testid={`testimonial-${index}`}>
                        <Card className="bg-white/10 backdrop-blur border-white/20">
                          <CardContent className="p-8">
                            <div className="flex gap-1 mb-5">
                              {[...Array(5)].map((_, i) => (
                                <Star key={i} className={`w-5 h-5 ${i < testimonial.rating ? 'fill-secondary text-secondary' : 'text-white/30'}`} />
                              ))}
                            </div>
                            <p className="text-white/90 text-lg italic leading-relaxed mb-6">"{testimonial.comment}"</p>
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                                <Users className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <p className="font-medium text-white">{testimonial.student_name}</p>
                                <p className="text-sm text-white/70">{testimonial.course}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Arrow buttons */}
                {items.length > 1 && (
                  <>
                    <button
                      onClick={prev}
                      className="absolute -left-5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
                      aria-label="Previous"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={next}
                      className="absolute -right-5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
                      aria-label="Next"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </>
                )}

                {/* Dot indicators */}
                {items.length > 1 && (
                  <div className="flex justify-center gap-2 mt-6">
                    {items.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setTestimonialIndex(i)}
                        className={`rounded-full transition-all duration-300 ${i === testimonialIndex ? 'w-6 h-2.5 bg-secondary' : 'w-2.5 h-2.5 bg-white/30 hover:bg-white/50'}`}
                        aria-label={`Go to testimonial ${i + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </section>}

      {/* Inquiry Form */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeUp}>
              <Badge className="mb-4">Admission Enquiry</Badge>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Start Your Success Journey with <span className="text-primary">Caliber Nova</span>
              </h2>
              <p className="text-muted-foreground mb-8">
                Join a learning environment that nurtures curiosity, strengthens concepts, and prepares students for future challenges. Book Your Admission Enquiry Today!
              </p>

              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">Call Us</p>
                    <p className="text-sm text-muted-foreground">+91 9595253778 | +91 9527225500</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">Email Us</p>
                    <p className="text-sm text-muted-foreground">info@calibernova.in</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">Visit Our Center</p>
                    <p className="text-sm text-muted-foreground">2nd Floor, Zenda Chowk, Mahal, Nagpur - 440032</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Card className="shadow-xl" data-testid="inquiry-form-card">
                <CardContent className="p-6 md:p-8">
                  <form onSubmit={handleInquirySubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        placeholder="Your Name"
                        value={inquiryForm.name}
                        onChange={(e) => setInquiryForm({ ...inquiryForm, name: e.target.value })}
                        required
                        data-testid="inquiry-name"
                      />
                      <Input
                        type="email"
                        placeholder="Email Address"
                        value={inquiryForm.email}
                        onChange={(e) => setInquiryForm({ ...inquiryForm, email: e.target.value })}
                        required
                        data-testid="inquiry-email"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        type="tel"
                        placeholder="Phone Number"
                        value={inquiryForm.phone}
                        onChange={(e) => setInquiryForm({ ...inquiryForm, phone: e.target.value })}
                        required
                        data-testid="inquiry-phone"
                      />
                      <Input
                        placeholder="Course Interest"
                        value={inquiryForm.course_interest}
                        onChange={(e) => setInquiryForm({ ...inquiryForm, course_interest: e.target.value })}
                        data-testid="inquiry-course"
                      />
                    </div>
                    <Textarea
                      placeholder="Your Message"
                      rows={4}
                      value={inquiryForm.message}
                      onChange={(e) => setInquiryForm({ ...inquiryForm, message: e.target.value })}
                      required
                      data-testid="inquiry-message"
                    />
                    <Button type="submit" className="w-full" disabled={submitting} data-testid="inquiry-submit">
                      {submitting ? 'Submitting...' : 'Submit Inquiry'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Notices Banner */}
      {notices.length > 0 && (
        <section className="py-8 bg-secondary">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex items-center gap-4 overflow-hidden">
              <Badge className="bg-primary text-white shrink-0">Latest</Badge>
              <div className="overflow-hidden">
                <p className="text-secondary-foreground font-medium whitespace-nowrap animate-marquee">
                  {notices.map(n => n.title).join(' • ')}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
};

export default HomePage;
